import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { videosAPI, getAssetUrl } from "../lib/api";
import { formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  CheckSquare,
  Clock,
  Check,
  X,
  Film,
  Hash,
  Calendar,
  FileText,
  Loader2,
  Send,
} from "lucide-react";

// TikTok Logo Component
const TikTokLogo = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
};

export default function ReviewQueuePage() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [postingToTikTok, setPostingToTikTok] = useState(null);

  const fetchPendingVideos = useCallback(async () => {
    try {
      const response = await videosAPI.getVideos("pending");
      setVideos(response.data);
      if (response.data.length > 0) {
        setSelectedVideo((prev) => prev || response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch pending videos:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingVideos();
  }, [fetchPendingVideos]);

  const handleApprove = async (videoId) => {
    setActionLoading(videoId);
    try {
      await videosAPI.approveVideo(videoId);
      toast.success("Video approved!");
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      if (selectedVideo?.id === videoId) {
        const remaining = videos.filter((v) => v.id !== videoId);
        setSelectedVideo(remaining[0] || null);
      }
    } catch (error) {
      toast.error("Failed to approve video");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (videoId) => {
    setActionLoading(videoId);
    try {
      await videosAPI.rejectVideo(videoId);
      toast.success("Video rejected");
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      if (selectedVideo?.id === videoId) {
        const remaining = videos.filter((v) => v.id !== videoId);
        setSelectedVideo(remaining[0] || null);
      }
    } catch (error) {
      toast.error("Failed to reject video");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePostToTikTok = async (videoId) => {
    setPostingToTikTok(videoId);
    // Simulate posting delay
    setTimeout(() => {
      toast.success("Video queued for TikTok posting!", {
        description: "Connect your TikTok account for real posting.",
        action: {
          label: "Connect",
          onClick: () => window.location.href = "/tiktok",
        },
      });
      setPostingToTikTok(null);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="review-queue-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <CheckSquare className="w-8 h-8 text-[#00F0FF]" />
          Review Queue
        </h1>
        <p className="text-muted-foreground mt-2">
          Review and approve videos before they go live.
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4">
        <Badge className="badge-trending gap-2 px-4 py-2">
          <Clock className="w-4 h-4" />
          {videos.length} Pending Review
        </Badge>
      </div>

      {videos.length === 0 ? (
        <Card className="bg-card border-border/50">
          <CardContent className="py-16 text-center">
            <CheckSquare className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground">No videos pending review. Great job!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Video List */}
          <div className="lg:col-span-4">
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Pending Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <AnimatePresence>
                    {videos.map((video) => (
                      <motion.div
                        key={video.id}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        layout
                        className={`p-4 border-b border-border/30 cursor-pointer transition-colors ${
                          selectedVideo?.id === video.id
                            ? "bg-[#00F0FF]/10 border-l-2 border-l-[#00F0FF]"
                            : "hover:bg-secondary/30"
                        }`}
                        onClick={() => setSelectedVideo(video)}
                        data-testid={`video-item-${video.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-12 bg-secondary rounded-lg flex-shrink-0 overflow-hidden">
                            {video.thumbnail_url ? (
                              <img
                                src={getAssetUrl(video.thumbnail_url)}
                                alt={video.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-white text-sm truncate">
                              {video.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(video.created_at)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Video Preview */}
          <div className="lg:col-span-8">
            {selectedVideo && (
              <motion.div
                key={selectedVideo.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-semibold text-white">
                        {selectedVideo.title}
                      </CardTitle>
                      <Badge className="badge-trending">Pending</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedVideo.created_at)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Thumbnail Preview */}
                    <div className="aspect-video bg-secondary rounded-xl overflow-hidden">
                      {selectedVideo.thumbnail_url ? (
                        <img
                          src={getAssetUrl(selectedVideo.thumbnail_url)}
                          alt={selectedVideo.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-16 h-16 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Script */}
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Script
                      </h4>
                      <ScrollArea className="h-48 p-4 bg-secondary/30 rounded-xl">
                        <pre className="text-sm text-white/90 whitespace-pre-wrap font-mono">
                          {selectedVideo.script}
                        </pre>
                      </ScrollArea>
                    </div>

                    {/* Hashtags */}
                    {selectedVideo.hashtags?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Hashtags</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVideo.hashtags.map((tag) => (
                            <Badge key={tag} variant="outline" className="badge-trending gap-1">
                              <Hash className="w-3 h-3" />
                              {tag.replace("#", "")}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-4 pt-4 border-t border-border/30">
                      <div className="flex gap-4">
                        <Button
                          onClick={() => handleApprove(selectedVideo.id)}
                          disabled={actionLoading === selectedVideo.id}
                          className="flex-1 bg-[#00FF94] hover:bg-[#00FF94]/90 text-[#0A0A0B] font-bold rounded-xl gap-2 neon-glow-green"
                          data-testid="approve-video-btn"
                        >
                          {actionLoading === selectedVideo.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(selectedVideo.id)}
                          disabled={actionLoading === selectedVideo.id}
                          variant="outline"
                          className="flex-1 border-[#FF0055]/50 text-[#FF0055] hover:bg-[#FF0055]/10 rounded-xl gap-2"
                          data-testid="reject-video-btn"
                        >
                          {actionLoading === selectedVideo.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          Reject
                        </Button>
                      </div>
                      {/* Post to TikTok button */}
                      <Button
                        onClick={() => handlePostToTikTok(selectedVideo.id)}
                        disabled={postingToTikTok === selectedVideo.id}
                        className="w-full bg-gradient-to-r from-[#FF0050] to-[#00F2EA] hover:opacity-90 text-white font-bold rounded-xl gap-2"
                        data-testid="post-to-tiktok-btn"
                      >
                        {postingToTikTok === selectedVideo.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <TikTokLogo className="w-4 h-4" />
                        )}
                        Post to TikTok
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
